// src/pages/EmployeeDetails.js
import { useEffect, useState, useMemo } from "react";
import { getEmployeeById, updateEmployeeById } from "../firebase/employees";
import {
  uploadEmployeeDocument,
  listEmployeeDocuments,
  deleteEmployeeDocument,
} from "../firebase/employeeDocuments";
import { useAuth } from "../Context/AuthContext";
import { useParams, useNavigate } from "react-router-dom";
import { IoChevronBack } from "react-icons/io5";
import AddressAutocomplete from "../components/AddressAutocomplete";
import { parsePlace } from "../utils/parsePlace";
import "../styles/EmployeeDetails.css";
import { db } from "../firebase/config";
import {
  doc,
  getDoc,
  updateDoc,
  arrayUnion,
  arrayRemove,
} from "firebase/firestore";

/* ───────────────────────── helpers ───────────────────────── */
const isValidPhone = (phone) =>
  /^\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/.test(phone);
const isValidDate = (d) => !isNaN(new Date(d).getTime());
const isFutureDate = (d) => new Date(d) > new Date();

const composeAddressLine = (f) =>
  [f.addressLine1, f.city, f.stateProvince, f.postalCode, f.country]
    .filter(Boolean)
    .join(", ") ||
  f.address ||
  "-";

// Optional free-typed geocode fallback
async function validateOrGeocodeAddressFallback({
  addressLine1,
  city,
  stateProvince,
  postalCode,
  country,
}) {
  if (
    ![addressLine1, city, stateProvince, postalCode, country]
      .filter(Boolean)
      .join("")
  ) {
    return null;
  }

  const query = [addressLine1, city, stateProvince, postalCode, country]
    .filter(Boolean)
    .join(", ");

  try {
    const url = new URL("https://maps.googleapis.com/maps/api/geocode/json");
    url.searchParams.set("address", query);
    url.searchParams.set(
      "key",
      process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ""
    );
    const res = await fetch(url.toString());
    const data = await res.json();
    if (data.status !== "OK" || !data.results?.length) return null;

    const r = data.results[0];
    const comps = r.address_components || [];
    const get = (...types) =>
      comps.find((c) => c.types?.some((t) => types.includes(t)));

    const number = get("street_number")?.long_name || "";
    const route = get("route")?.long_name || "";
    const normalizedStreet =
      number && route ? `${number} ${route}` : route || addressLine1;

    return {
      addressLine1: normalizedStreet,
      city:
        get("locality")?.long_name ||
        get("sublocality")?.long_name ||
        get("administrative_area_level_2")?.long_name ||
        city,
      stateProvince:
        get("administrative_area_level_1")?.short_name ||
        get("administrative_area_level_1")?.long_name ||
        stateProvince,
      postalCode: get("postal_code")?.long_name || postalCode,
      country: get("country")?.long_name || country,
      addressGeo: {
        lat: r.geometry?.location?.lat ?? null,
        lng: r.geometry?.location?.lng ?? null,
      },
    };
  } catch {
    return null;
  }
}

const DEFAULT_GROUPS = ["IT", "Finance", "Sales", "Marketing", "Operations"];

/* ───────────────────── status + completeness UI ───────────────────── */
function StatusPill({ status = "" }) {
  const s = (status || "").toLowerCase();
  const cls =
    s === "active"
      ? "status-pill is-active"
      : s === "terminated"
      ? "status-pill is-terminated"
      : "status-pill is-pending";
  const label = s ? s[0].toUpperCase() + s.slice(1) : "—";
  return (
    <span className={cls} role="status" aria-label={`Status: ${label}`}>
      {label}
    </span>
  );
}

function IncompleteBadge({ title = "Profile incomplete" }) {
  return (
    <span
      className="incomplete-badge"
      title={title}
      aria-label={title}
      role="status"
    >
      <span className="incomplete-dot" aria-hidden="true" /> Incomplete
    </span>
  );
}

// fields required for “complete”
const REQUIRED_FIELDS = [
  "preferredFirstName",
  "phone",
  "gender",
  "addressLine1",
  "birthDate",
  "jobTitle",
  "group",
  "hireDate",
];

function getMissingFields(form) {
  return REQUIRED_FIELDS.filter((k) => !String(form?.[k] ?? "").trim());
}
function getCompletionPct(form) {
  const missing = getMissingFields(form);
  return Math.round(
    ((REQUIRED_FIELDS.length - missing.length) / REQUIRED_FIELDS.length) * 100
  );
}

/* ───────────────────────── component ───────────────────────── */
export default function EmployeeDetails() {
  const { user, loading } = useAuth();
  const companyId = user?.companyId;
  const { employeeId } = useParams();
  const navigate = useNavigate();

  const [employee, setEmployee] = useState(null);
  const [error, setError] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({ helpdeskRole: "" });
  const [successMsg, setSuccessMsg] = useState("");
  const [activeTab, setActiveTab] = useState("profile");
  const [documents, setDocuments] = useState([]);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewType, setPreviewType] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [missingFieldsMsg, setMissingFieldsMsg] = useState("");
  const [errors, setErrors] = useState({});
  const [groupOptions, setGroupOptions] = useState([...DEFAULT_GROUPS]);
  const [groupMode, setGroupMode] = useState("select");
  const [customGroup, setCustomGroup] = useState("");

  const HELP_DESK_ROLES = [
    { value: "agent", label: "Helpdesk Agent" },
    { value: "manager", label: "Helpdesk Manager" },
  ];

  // fetch employee
  useEffect(() => {
    if (!loading && companyId && employeeId) {
      (async () => {
        try {
          const emp = await getEmployeeById(companyId, employeeId);
          setEmployee(emp);
        } catch (err) {
          console.error("Error loading employee:", err.message);
          setError("Unable to load employee details.");
        }
      })();
    }
  }, [loading, companyId, employeeId]);

  // fetch company groups
  useEffect(() => {
    if (!loading && companyId) {
      (async () => {
        try {
          const snap = await getDoc(doc(db, "companies", companyId));
          if (snap.exists()) {
            const data = snap.data();
            const customs = Array.isArray(data.groups)
              ? data.groups.filter((g) => !DEFAULT_GROUPS.includes(g))
              : [];
            setGroupOptions([...DEFAULT_GROUPS, ...customs]);
          }
        } catch (e) {
          console.error("Failed to load company groups:", e);
        }
      })();
    }
  }, [loading, companyId]);

  // seed form
  useEffect(() => {
    if (!employee) return;

    setFormData({
      name: employee.name || "",
      middleName: employee.middleName || "",
      lastName: employee.lastName || "",
      preferredFirstName: employee.preferredFirstName || "",
      email: employee.email || "",
      phone: employee.phone || "",
      gender: employee.gender || "",
      address: employee.address || "",
      addressLine1: employee.addressDetails?.line1 || employee.address || "",
      city: employee.addressDetails?.city || "",
      stateProvince: employee.addressDetails?.stateProvince || "",
      postalCode: employee.addressDetails?.postalCode || "",
      country: employee.addressDetails?.country || "",
      addressGeo:
        employee.addressGeo && employee.addressGeo.lat != null
          ? employee.addressGeo
          : null,
      birthDate: employee.birthDate || "",
      role: employee.role || "",
      jobTitle: employee.jobTitle || "",
      group: employee.group || "",
      helpdeskRole: employee.helpdeskRole || "",
      hireDate: employee.hireDate || "",
      status: employee.status || "",
      profileCompleted: Boolean(employee.profileCompleted),

      employmentType: employee.employmentType || "full_time",
      startDate: employee.startDate || employee.hireDate || "",
      endDate: employee.endDate || "",
      ongoing: typeof employee.ongoing === "boolean" ? employee.ongoing : true,

      // pay
      payRate: employee.payRate ?? "",
    });
  }, [employee]);

  // docs on tab open
  useEffect(() => {
    if (activeTab !== "documents" || !companyId || !employeeId) return;
    (async () => {
      try {
        const files = await listEmployeeDocuments(companyId, employeeId);
        setDocuments(files);
      } catch (err) {
        console.error("Error loading documents:", err.message);
      }
    })();
  }, [activeTab, companyId, employeeId]);

  const reloadDocuments = async () => {
    try {
      const files = await listEmployeeDocuments(companyId, employeeId);
      setDocuments(files);
    } catch (err) {
      console.error("Error loading documents:", err.message);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setMissingFieldsMsg("");
    setErrors({});
    const newErrors = {};

    // required validation
    const required = [...REQUIRED_FIELDS];
    if ((formData.group || "").toUpperCase() === "IT")
      required.push("helpdeskRole");

    const missing = required.filter(
      (k) => !formData[k] || String(formData[k]).trim() === ""
    );

    if (formData.phone && !isValidPhone(formData.phone)) {
      newErrors.phone = "Invalid phone number format.";
    }

    if (
      formData.birthDate &&
      (!isValidDate(formData.birthDate) || isFutureDate(formData.birthDate))
    ) {
      newErrors.birthDate = "Invalid or future birth date.";
    }

    // Pay rate validation (optional field but must be valid if entered)
    if (formData.payRate !== "" && formData.payRate != null) {
      const n = Number(formData.payRate);
      if (Number.isNaN(n) || n < 0) {
        newErrors.payRate = "Enter a valid non-negative hourly rate.";
      }
    }

    if (formData.status === "Active") {
      missing.forEach((k) => (newErrors[k] = "This field is required."));
    }

    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      setMissingFieldsMsg("Please fix the errors above.");
      setIsSaving(false);
      return;
    }

    try {
      let enriched = {};
      if (!formData.addressGeo) {
        const fallback = await validateOrGeocodeAddressFallback({
          addressLine1: formData.addressLine1,
          city: formData.city,
          stateProvince: formData.stateProvince,
          postalCode: formData.postalCode,
          country: formData.country,
        });
        if (fallback) enriched = fallback;
      }

      const merged = { ...formData, ...enriched };
      const legacyAddress = merged.address || composeAddressLine(merged) || "";

      const willBeComplete = getMissingFields(merged).length === 0;

      // convert payRate to number or null
      let payRateValue = null;
      if (merged.payRate !== "" && merged.payRate != null) {
        const n = Number(merged.payRate);
        payRateValue = !Number.isNaN(n) ? n : null;
      }

      const payload = {
        ...merged,
        payRate: payRateValue,

        helpdeskRole:
          (merged.group || "").toUpperCase() === "IT"
            ? merged.helpdeskRole || ""
            : "",

        address: legacyAddress,
        addressDetails: {
          line1: merged.addressLine1 || "",
          city: merged.city || "",
          stateProvince: merged.stateProvince || "",
          postalCode: merged.postalCode || "",
          country: merged.country || "",
        },
        addressGeo:
          merged.addressGeo &&
          merged.addressGeo.lat != null &&
          merged.addressGeo.lng != null
            ? merged.addressGeo
            : null,

        profileCompleted: willBeComplete,
      };

      await updateEmployeeById(companyId, employeeId, payload);
      setEmployee((prev) => ({ ...prev, ...payload }));
      setEditMode(false);
      setSuccessMsg("Employee details updated successfully.");
      setTimeout(() => setSuccessMsg(""), 2500);
    } catch (err) {
      console.error("Error updating employee:", err.message);
      alert("Failed to update employee.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      await uploadEmployeeDocument(companyId, employeeId, file);
      await reloadDocuments();
    } catch (err) {
      console.error("Upload failed:", err);
      alert("Failed to upload document.");
    }
  };

  const handleDeleteDocument = async (fullPath) => {
    try {
      await deleteEmployeeDocument(fullPath);
      await reloadDocuments();
    } catch (err) {
      console.error("Delete failed:", err);
      alert("Failed to delete document.");
    }
  };

  const renderInput = (key, type = "text", placeholder = "") => (
    <div className="ed-input-wrap">
      <input
        type={type}
        value={formData[key] || ""}
        onChange={(e) => setFormData({ ...formData, [key]: e.target.value })}
        className={`ed-input ${errors[key] ? "ed-input-error" : ""}`}
        placeholder={placeholder}
      />
      {errors[key] && <p className="ed-error">{errors[key]}</p>}
    </div>
  );

  const renderSelect = (key, options) => (
    <div className="ed-input-wrap">
      <select
        className={`ed-input ${errors[key] ? "ed-input-error" : ""}`}
        value={formData[key] || ""}
        onChange={(e) => setFormData({ ...formData, [key]: e.target.value })}
      >
        <option value="">Select</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
      {errors[key] && <p className="ed-error">{errors[key]}</p>}
    </div>
  );

  const fullName = useMemo(
    () => [formData.name, formData.lastName].filter(Boolean).join(" "),
    [formData.name, formData.lastName]
  );

  if (loading || !user) return <p>Loading...</p>;
  if (error) return <p style={{ color: "red" }}>{error}</p>;
  if (!employee) return null;

  return (
    <div className="ed-wrap">
      {/* Back pill */}
      <button
        className="ed-back"
        onClick={() =>
          navigate("/admin-dashboard", {
            state: { tab: "employee-management" },
          })
        }
      >
        <IoChevronBack /> <span>Employee List</span>
      </button>

      {/* Page header with status + completeness */}
      <div className="ed-head">
        <div className="ed-head-left">
          <div className="ed-avatar">
            {(employee?.name || "U").charAt(0).toUpperCase()}
          </div>
          <div className="ed-title-stack">
            <h1>{fullName || "Employee"}</h1>
            <div className="ed-meta-row">
              <StatusPill status={formData.status} />
              {!formData.profileCompleted && <IncompleteBadge />}
              <div className="ed-progress" aria-label="Profile completion">
                <div
                  className="ed-progress-bar"
                  style={{ width: `${getCompletionPct(formData)}%` }}
                />
              </div>
              <span className="ed-progress-label">
                {getCompletionPct(formData)}% complete
              </span>
            </div>
          </div>
        </div>

        <div className="ed-head-right">
          {editMode && (
            <select
              className="ed-input ed-input--condensed"
              value={formData.status || ""}
              onChange={(e) =>
                setFormData((p) => ({ ...p, status: e.target.value }))
              }
            >
              <option value="">Select status</option>
              <option value="Active">Active</option>
              <option value="Pending">Pending</option>
              <option value="Terminated">Terminated</option>
            </select>
          )}

          {/* Top Edit / Save / Cancel */}
          <div className="ed-actions-top">
            {!editMode ? (
              <button className="btn-primary" onClick={() => setEditMode(true)}>
                Edit
              </button>
            ) : (
              <>
                <button
                  className="btn-primary"
                  onClick={handleSave}
                  disabled={isSaving}
                >
                  {isSaving ? "Saving…" : "Save"}
                </button>

                <button
                  className="btn-cancel"
                  onClick={() => {
                    setEditMode(false);
                    setErrors({});
                    setMissingFieldsMsg("");
                  }}
                >
                  Cancel
                </button>
              </>
            )}
          </div>

          {missingFieldsMsg && editMode && (
            <div className="ed-warning ed-warning-inline">
              ⚠ {missingFieldsMsg}
            </div>
          )}

          {successMsg && <div className="ed-toast">{successMsg}</div>}
        </div>
      </div>

      {/* Tabs */}
      <div className="ed-tabs">
        <button
          className={activeTab === "profile" ? "active" : ""}
          onClick={() => setActiveTab("profile")}
        >
          Profile
        </button>
        <button
          className={activeTab === "documents" ? "active" : ""}
          onClick={() => setActiveTab("documents")}
        >
          Documents
        </button>
      </div>

      {activeTab === "profile" && (
        <>
          {/* Personal */}
          <section className="ed-card">
            <h2 className="ed-section">Personal Info</h2>
            <div className="ed-grid">
              <div className="kv">
                <label>First Name</label>
                {editMode ? renderInput("name") : <p>{formData.name || "-"}</p>}
              </div>

              <div className="kv">
                <label>Middle Name</label>
                {editMode ? (
                  renderInput("middleName")
                ) : (
                  <p>{formData.middleName || "-"}</p>
                )}
              </div>

              <div className="kv">
                <label>Last Name</label>
                {editMode ? (
                  renderInput("lastName")
                ) : (
                  <p>{formData.lastName || "-"}</p>
                )}
              </div>

              <div className="kv">
                <label>Preferred First Name</label>
                {editMode ? (
                  renderInput("preferredFirstName")
                ) : (
                  <p>{formData.preferredFirstName || "-"}</p>
                )}
              </div>

              <div className="kv">
                <label>Email</label>
                <p>{formData.email || "-"}</p>
              </div>

              <div className="kv">
                <label>Phone</label>
                {editMode ? (
                  renderInput("phone", "text", "(555) 555-5555")
                ) : (
                  <p>{formData.phone || "-"}</p>
                )}
              </div>

              <div className="kv">
                <label>Gender</label>
                {editMode ? (
                  renderSelect("gender", ["Male", "Female", "Other"])
                ) : (
                  <p>{formData.gender || "-"}</p>
                )}
              </div>

              {/* Address picker + structured fields */}
              <div className="kv" style={{ gridColumn: "1 / -1" }}>
                <label>Address</label>
                {editMode ? (
                  <>
                    <AddressAutocomplete
                      value={formData.addressLine1 || ""}
                      onChange={(val) =>
                        setFormData((p) => ({ ...p, addressLine1: val }))
                      }
                      onSelect={(place) => {
                        if (!place) return;
                        const p = parsePlace(place);
                        setFormData((prev) => ({
                          ...prev,
                          addressLine1: p.street || "",
                          city: p.city || "",
                          stateProvince: p.stateProvince || "",
                          postalCode: p.postalCode || "",
                          country: p.country || "",
                          addressGeo:
                            p.lat != null && p.lng != null
                              ? { lat: p.lat, lng: p.lng }
                              : null,
                        }));
                      }}
                      placeholder="Start typing your address…"
                    />

                    <div className="ed-grid" style={{ marginTop: 12 }}>
                      <div className="kv">
                        <label>City</label>
                        <div className="ed-input-wrap">
                          <input
                            className="ed-input"
                            value={formData.city || ""}
                            onChange={(e) =>
                              setFormData((p) => ({
                                ...p,
                                city: e.target.value,
                              }))
                            }
                          />
                        </div>
                      </div>

                      <div className="kv">
                        <label>State / Province</label>
                        <div className="ed-input-wrap">
                          <input
                            className="ed-input"
                            value={formData.stateProvince || ""}
                            onChange={(e) =>
                              setFormData((p) => ({
                                ...p,
                                stateProvince: e.target.value,
                              }))
                            }
                          />
                        </div>
                      </div>

                      <div className="kv">
                        <label>Postal Code</label>
                        <div className="ed-input-wrap">
                          <input
                            className="ed-input"
                            value={formData.postalCode || ""}
                            onChange={(e) =>
                              setFormData((p) => ({
                                ...p,
                                postalCode: e.target.value,
                              }))
                            }
                          />
                        </div>
                      </div>

                      <div className="kv">
                        <label>Country</label>
                        <div className="ed-input-wrap">
                          <input
                            className="ed-input"
                            value={formData.country || ""}
                            onChange={(e) =>
                              setFormData((p) => ({
                                ...p,
                                country: e.target.value,
                              }))
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <p>{composeAddressLine(formData)}</p>
                )}
              </div>

              <div className="kv">
                <label>Birth Date</label>
                {editMode ? (
                  renderInput("birthDate", "date")
                ) : (
                  <p>{formData.birthDate || "-"}</p>
                )}
              </div>
            </div>
          </section>

          {/* Employment */}
          <section className="ed-card">
            <h2 className="ed-section">Employment Details</h2>
            <div className="ed-grid">
              <div className="kv">
                <label>Role</label>
                {editMode ? (
                  renderSelect("role", ["employee"])
                ) : (
                  <p>{formData.role || "-"}</p>
                )}
              </div>

              <div className="kv">
                <label>Job Title</label>
                {editMode ? (
                  renderInput("jobTitle")
                ) : (
                  <p>{formData.jobTitle || "-"}</p>
                )}
              </div>

              <div className="kv" style={{ gridColumn: "1 / -1" }}>
                <label>Group / Department</label>
                {!editMode ? (
                  <p>{formData.group || "-"}</p>
                ) : groupMode === "select" ? (
                  <div className="ed-input-wrap">
                    <select
                      className={`ed-input ${
                        errors.group ? "ed-input-error" : ""
                      }`}
                      value={formData.group || ""}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === "__custom__") {
                          setGroupMode("custom");
                          return;
                        }
                        setFormData((p) => ({
                          ...p,
                          group: v,
                          helpdeskRole:
                            (v || "").toUpperCase() === "IT"
                              ? p.helpdeskRole || ""
                              : "",
                        }));
                      }}
                    >
                      <option value="">Select Group</option>
                      {groupOptions.map((g) => (
                        <option key={g} value={g}>
                          {g}
                        </option>
                      ))}
                      <option value="__custom__">+ Add custom…</option>
                    </select>
                    {errors.group && <p className="ed-error">{errors.group}</p>}
                  </div>
                ) : (
                  <div
                    className="ed-input-wrap"
                    style={{ display: "grid", gap: 8 }}
                  >
                    <div style={{ display: "flex", gap: 8 }}>
                      <input
                        className="ed-input"
                        placeholder="Enter a new group (e.g., Customer Success)"
                        value={customGroup}
                        onChange={(e) => setCustomGroup(e.target.value)}
                      />
                      <button
                        type="button"
                        className="btn-primary"
                        onClick={() => addCustomGroupToCompany(customGroup)}
                      >
                        Add
                      </button>
                      <button
                        type="button"
                        className="btn-ghost"
                        onClick={() => {
                          setCustomGroup("");
                          setGroupMode("select");
                        }}
                      >
                        Cancel
                      </button>
                    </div>

                    <div>
                      <p className="ed-muted" style={{ marginBottom: 6 }}>
                        Existing Custom Groups
                      </p>
                      {groupOptions
                        .filter((g) => !DEFAULT_GROUPS.includes(g))
                        .map((g) => (
                          <div
                            key={g}
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              background: "#f7f7f8",
                              padding: "6px 10px",
                              borderRadius: 6,
                              marginBottom: 6,
                            }}
                          >
                            <span>{g}</span>
                            <button
                              type="button"
                              className="btn-link danger"
                              onClick={() => deleteCustomGroupFromCompany(g)}
                            >
                              Delete
                            </button>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>

              {((formData.group || "").toUpperCase() === "IT" ||
                (customGroup || "").toUpperCase() === "IT") && (
                <div className="kv">
                  <label>Help Desk Role</label>
                  {editMode ? (
                    <div className="ed-input-wrap">
                      <select
                        className={`ed-input ${
                          errors.helpdeskRole ? "ed-input-error" : ""
                        }`}
                        value={formData.helpdeskRole || ""}
                        onChange={(e) =>
                          setFormData((p) => ({
                            ...p,
                            helpdeskRole: e.target.value,
                          }))
                        }
                      >
                        <option value="">Select</option>
                        {HELP_DESK_ROLES.map((r) => (
                          <option key={r.value} value={r.value}>
                            {r.label}
                          </option>
                        ))}
                      </select>
                      {errors.helpdeskRole && (
                        <p className="ed-error">{errors.helpdeskRole}</p>
                      )}
                    </div>
                  ) : (
                    <p>
                      {formData.helpdeskRole
                        ? HELP_DESK_ROLES.find(
                            (r) => r.value === formData.helpdeskRole
                          )?.label
                        : "-"}
                    </p>
                  )}
                </div>
              )}

              <div className="kv">
                <label>Employment</label>
                <p>
                  {formData.employmentType === "part_time"
                    ? "Part-time"
                    : "Full-time"}
                  {" • "}
                  {formData.startDate || formData.hireDate || "-"}
                  {formData.employmentType === "part_time" &&
                    (formData.endDate
                      ? ` → ${formData.endDate}`
                      : " → Ongoing")}
                </p>
              </div>
            </div>

            {/* Pay rate row */}
            <div className="kv">
              <label>Pay Rate (per hour)</label>
              {editMode ? (
                <div className="ed-input-wrap">
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className={`ed-input ${
                      errors.payRate ? "ed-input-error" : ""
                    }`}
                    value={formData.payRate ?? ""}
                    onChange={(e) =>
                      setFormData((p) => ({ ...p, payRate: e.target.value }))
                    }
                    placeholder="e.g. 20.00"
                  />
                  {errors.payRate && (
                    <p className="ed-error">{errors.payRate}</p>
                  )}
                </div>
              ) : (
                <p>
                  {formData.payRate !== "" && formData.payRate != null
                    ? `$${Number(formData.payRate).toFixed(2)} / hr`
                    : "-"}
                </p>
              )}
            </div>
          </section>
        </>
      )}

      {activeTab === "documents" && (
        <section className="ed-card">
          <h2 className="ed-section">Documents</h2>

          <div className="ed-docs-bar">
            <label className="btn-outline">
              Upload File
              <input
                type="file"
                accept=".pdf,.doc,.docx,.png,.jpg"
                onChange={handleFileUpload}
                hidden
              />
            </label>
          </div>

          {documents.length === 0 ? (
            <p className="ed-muted">No documents uploaded yet.</p>
          ) : (
            <ul className="ed-filelist">
              {documents.map((d) => {
                const isImage = /\.(png|jpg|jpeg)$/i.test(d.name);
                const isPdf = /\.pdf$/i.test(d.name);

                return (
                  <li key={d.name} className="file-row">
                    <button
                      className="file-link"
                      onClick={() => {
                        if (isImage || isPdf) {
                          setPreviewUrl(d.url);
                          setPreviewType(isImage ? "image" : "pdf");
                        } else {
                          window.open(d.url, "_blank");
                        }
                      }}
                    >
                      {d.name}
                    </button>

                    <button
                      className="file-delete"
                      onClick={() => handleDeleteDocument(d.fullPath)}
                    >
                      Delete
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      )}

      {/* Preview modal */}
      {previewUrl && (
        <div className="ed-modal">
          <div className="ed-modal-body">
            <button className="ed-close" onClick={() => setPreviewUrl(null)}>
              ✖
            </button>

            {previewType === "image" ? (
              <img src={previewUrl} alt="Preview" className="ed-preview-img" />
            ) : (
              <iframe
                src={previewUrl}
                title="PDF Preview"
                className="ed-preview-pdf"
              />
            )}
          </div>
        </div>
      )}
    </div>
  );

  // ───────────────────────── custom groups ─────────────────────────
  async function addCustomGroupToCompany(label) {
    const val = (label || "").trim();
    if (!val) return;

    const exists = groupOptions.some(
      (x) => x.toLowerCase() === val.toLowerCase()
    );
    if (exists) {
      const canonical = groupOptions.find(
        (x) => x.toLowerCase() === val.toLowerCase()
      );
      setFormData((p) => ({ ...p, group: canonical || p.group }));
      setCustomGroup("");
      setGroupMode("select");
      return;
    }

    if (DEFAULT_GROUPS.includes(val)) {
      setFormData((p) => ({ ...p, group: val }));
      setCustomGroup("");
      setGroupMode("select");
      return;
    }

    try {
      await updateDoc(doc(db, "companies", companyId), {
        groups: arrayUnion(val),
      });
      setGroupOptions((prev) => [...prev, val]);
      setFormData((p) => ({ ...p, group: val }));
      setCustomGroup("");
      setGroupMode("select");
    } catch (e) {
      console.error("Could not add custom group:", e);
      setError("Could not add custom group. Try again.");
    }
  }

  async function deleteCustomGroupFromCompany(label) {
    try {
      await updateDoc(doc(db, "companies", companyId), {
        groups: arrayRemove(label),
      });
      setGroupOptions((prev) => prev.filter((g) => g !== label));
      setFormData((p) => ({ ...p, group: p.group === label ? "" : p.group }));
    } catch (e) {
      console.error("Could not delete custom group:", e);
      setError("Could not delete custom group. Try again.");
    }
  }
}
